import { useState } from "react";
import { emitIntentEvent } from "./intentEvents";

export interface IntentOption {
  value: number;
  label: string;
}

export const ARTIST_INTENTS: IntentOption[] = [
  { value: 32, label: "Sell my work" },
  { value: 1, label: "Build my website" },
  { value: 4, label: "Promote my work" },
  { value: 8, label: "Manage my buyers" },
  { value: 16, label: "Run my business" },
  { value: 2, label: "Email my buyers" },
  { value: 64, label: "Meet new galleries" },
];

// Same SignupIntent bits, gallery voice. MeetNewGalleries (64) is artist-only:
// a gallery is the counterparty, not the seeker.
//
// `venue` is User.getTypeDisplay(user) lowercased — "gallery", or "boutique" for
// a jewelry signup. Computed by the caller because Type.jsx has named exports
// that global.d.ts does not declare.
export function galleryIntents(venue: string): IntentOption[] {
  const v = venue || "gallery";
  return [
    { value: 32, label: "Sell on the marketplace" },
    { value: 1, label: `Create ${v} website` },
    { value: 4, label: "Create collateral" },
    { value: 8, label: "Manage our collectors" },
    { value: 16, label: "Run our business" },
    { value: 2, label: "Send email campaigns" },
  ];
}

/**
 * The entry path every intent beacon is tagged with. Its own function rather
 * than a literal at the one call site below: the rebuilt gallery step used to
 * tag its "Skip for now" beacon with this too, and a second literal "organic"
 * would have split the funnel in two the day either one was edited.
 */
function intentEntryPath(isArtistReferral?: boolean): string {
  return isArtistReferral ? "artlogic-artist" : "organic";
}

export interface UseIntentSelectionOptions {
  options: IntentOption[];
  isArtistReferral?: boolean;
  /**
   * Seeds the bitmask on mount, for a step reopened with an answer already on file (ACA-266's
   * ghost-that-returns). Never re-derives click order from it - a bitmask does not carry one, so
   * the container that owns this seed is the one that reconstructs a label order, from a goal
   * list's own canonical order.
   */
  initialIntent?: number;
  onContinue: (intent: number) => void;
  onScrollTop?: () => void;
}

export interface IntentSelection {
  /** Combined bitmask of the currently selected intents. */
  intent: number;
  /** True when a submit was attempted with nothing selected. */
  error: boolean;
  /** Toggle a single chip on/off (emits the select/deselect beacon). */
  toggle: (value: number) => void;
  /** Validate, emit the submit beacon, and forward the bitmask to onContinue. */
  submit: () => void;
}

/**
 * Shared intent-selection state and analytics, used by both the artist card
 * (IntentScreen) and the legacy gallery intent step so the two layouts stay in
 * lockstep on behaviour while differing in presentation and in which option
 * set they pass in.
 */
export function useIntentSelection({
  options,
  isArtistReferral,
  initialIntent,
  onContinue,
  onScrollTop,
}: UseIntentSelectionOptions): IntentSelection {
  const [intent, setIntent] = useState(initialIntent ?? 0);
  const [error, setError] = useState(false);
  const entryPath = intentEntryPath(isArtistReferral);

  const toggle = (value: number) => {
    const isSelected = (intent & value) !== 0;
    const next = isSelected ? intent & ~value : intent | value;
    setIntent(next);
    if (error && next !== 0) {
      setError(false);
    }
    const totalSelected = options.filter((p) => (next & p.value) !== 0).length;
    emitIntentEvent(isSelected ? "intent_deselected" : "intent_selected", {
      intent: value,
      totalSelected,
      entryPath,
      isArtistReferral: !!isArtistReferral,
    });
  };

  const submit = () => {
    if (intent === 0) {
      setError(true);
    } else {
      setError(false);
      emitIntentEvent("onboarding_submitted", {
        intents: intent,
        entryPath,
        isArtistReferral: !!isArtistReferral,
      });
      onContinue(intent);
    }
    onScrollTop?.();
  };

  return { intent, error, toggle, submit };
}
